import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';

const TICKER_SYMBOLS = [
  { id: 'BTC', name: 'Bitcoin', symbol: 'BTCUSDT', color: '#f7931a' },
  { id: 'ETH', name: 'Ethereum', symbol: 'ETHUSDT', color: '#627eea' },
  { id: 'SPY', name: 'S&P 500', symbol: 'SPY', color: '#22c55e' },
  { id: 'QQQ', name: 'Nasdaq', symbol: 'QQQ', color: '#3b82f6' },
  { id: 'SOL', name: 'Solana', symbol: 'SOLUSDT', color: '#9945ff' },
  { id: 'XAU', name: 'Gold', symbol: 'XAUUSD', color: '#fcd34d' },
];

const POLL_INTERVAL = 30000; // 30 seconds

export default function PriceRunner() {
  const [prices, setPrices] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPrices = useCallback(async () => {
    try {
      // Fetch crypto prices from CoinGecko (free, no key required)
      const cryptoIds = 'bitcoin,ethereum,solana';
      const cryptoRes = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${cryptoIds}&vs_currencies=usd&include_24hr_change=true`,
        { cache: 'no-store' }
      );
      
      if (cryptoRes.ok) {
        const cryptoData = await cryptoRes.json();
        
        setPrices(prev => ({
          ...prev,
          BTC: {
            price: cryptoData.bitcoin?.usd || prev?.BTC?.price || 0,
            change: cryptoData.bitcoin?.usd_24h_change || 0,
          },
          ETH: {
            price: cryptoData.ethereum?.usd || prev?.ETH?.price || 0,
            change: cryptoData.ethereum?.usd_24h_change || 0,
          },
          SOL: {
            price: cryptoData.solana?.usd || prev?.SOL?.price || 0,
            change: cryptoData.solana?.usd_24h_change || 0,
          },
        }));
      }
      
      // For stocks/gold, use mock data with realistic values
      // In production, integrate Yahoo Finance or Alpha Vantage
      setPrices(prev => ({
        ...prev,
        SPY: prev?.SPY || { price: 598.45, change: 0.34 },
        QQQ: prev?.QQQ || { price: 525.82, change: 0.56 },
        XAU: prev?.XAU || { price: 2645.30, change: -0.12 },
      }));
      
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.warn('Price fetch failed:', err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  const formatPrice = (price, symbol) => {
    if (!price) return '--';
    if (symbol === 'BTC') return `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (symbol === 'ETH' || symbol === 'SOL') return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (symbol === 'XAU') return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const TrendIcon = ({ change }) => {
    if (change > 0.1) return <TrendingUp className="w-3 h-3 text-green-400" />;
    if (change < -0.1) return <TrendingDown className="w-3 h-3 text-red-400" />;
    return <Minus className="w-3 h-3 text-zinc-500" />;
  };

  return (
    <div 
      className="bg-zinc-900/80 border-b border-zinc-800 backdrop-blur-md"
      data-testid="price-runner"
    >
      <div className="flex items-center justify-between px-4 py-1.5 overflow-x-auto">
        {/* Ticker tape */}
        <div className="flex items-center gap-6 animate-fade-in">
          {TICKER_SYMBOLS.map(ticker => {
            const data = prices[ticker.id];
            const change = data?.change || 0;
            const changeColor = change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-zinc-500';
            
            return (
              <div 
                key={ticker.id}
                className="flex items-center gap-2 min-w-fit"
                data-testid={`ticker-${ticker.id.toLowerCase()}`}
              >
                <span 
                  className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: ticker.color }}
                >
                  {ticker.id}
                </span>
                <span className="text-xs font-mono text-white">
                  {loading ? '...' : formatPrice(data?.price, ticker.id)}
                </span>
                <div className="flex items-center gap-0.5">
                  <TrendIcon change={change} />
                  <span className={`text-[10px] font-mono ${changeColor}`}>
                    {loading ? '' : `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Last update indicator */}
        <div className="flex items-center gap-2 text-[10px] text-zinc-500 ml-4 flex-shrink-0">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">
            {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : 'Loading...'}
          </span>
        </div>
      </div>
    </div>
  );
}
